import { useRef, useState } from "react";
import {
  Box,
  ButtonGroup,
  Button as MuiButton,
  ClickAwayListener,
  Divider,
  Grow,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import { useAppTheme } from "@/hooks/useAppTheme";

interface QuickAction {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}

interface LandingPageHeaderProps {
  userName: string;
  onUploadClick?: () => void;
  onCreateTaskClick?: () => void;
  onCreateClientClick?: () => void;
  onCreatePolicyClick?: () => void;
  onCreateVendorClick?: () => void;
  showUpload?: boolean;
  showCreateTask?: boolean;
  showCreateClient?: boolean;
  showCreatePolicy?: boolean;
  showCreateVendor?: boolean;
}

const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const formatDate = (): string => {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
};

export const LandingPageHeader = ({
  userName,
  onUploadClick,
  onCreateTaskClick,
  onCreateClientClick,
  onCreatePolicyClick,
  onCreateVendorClick,
  showUpload = false,
  showCreateTask = false,
  showCreateClient = false,
  showCreatePolicy = false,
  showCreateVendor = false,
}: LandingPageHeaderProps) => {
  const { isDarkMode, logoImage, logoImageDark } = useAppTheme();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleClose = () => setOpen(false);

  const makeHandler = (fn?: () => void) => () => {
    handleClose();
    fn?.();
  };

  // Build action groups — only include permitted actions
  const uploadActions: QuickAction[] = [];
  if (showUpload && onUploadClick) {
    uploadActions.push({
      label: "Upload File",
      icon: <CloudUploadOutlinedIcon fontSize="small" />,
      onClick: makeHandler(onUploadClick),
    });
  }

  const createActions: QuickAction[] = [];
  if (showCreateTask && onCreateTaskClick) {
    createActions.push({
      label: "New Task",
      icon: <ChecklistOutlinedIcon fontSize="small" />,
      onClick: makeHandler(onCreateTaskClick),
    });
  }
  if (showCreateClient && onCreateClientClick) {
    createActions.push({
      label: "New Client",
      icon: <CorporateFareOutlinedIcon fontSize="small" />,
      onClick: makeHandler(onCreateClientClick),
    });
  }
  if (showCreatePolicy && onCreatePolicyClick) {
    createActions.push({
      label: "New Policy",
      icon: <PolicyOutlinedIcon fontSize="small" />,
      onClick: makeHandler(onCreatePolicyClick),
    });
  }
  if (showCreateVendor && onCreateVendorClick) {
    createActions.push({
      label: "New Vendor",
      icon: <LocalHospitalOutlinedIcon fontSize="small" />,
      onClick: makeHandler(onCreateVendorClick),
    });
  }

  const allActions = [...uploadActions, ...createActions];
  const hasActions = allActions.length > 0;

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        {(logoImage || logoImageDark) && (
          <Box
            component="img"
            src={(isDarkMode ? logoImageDark || logoImage : logoImage) ?? undefined}
            alt="Partner logo"
            sx={{ height: 48, width: "auto", objectFit: "contain" }}
          />
        )}
        <Box>
          <Typography variant="caption" color="text.disabled" fontWeight={500} sx={{ letterSpacing: "0.04em" }}>
            {formatDate()}
          </Typography>
          <Typography variant="h2" sx={{ mt: 0.25 }}>
            {getGreeting()}, {userName}
          </Typography>
        </Box>
      </Stack>

      {hasActions && (
        <Box>
          <ButtonGroup ref={anchorRef} variant="outlined" size="small">
            <MuiButton onClick={() => setOpen((prev) => !prev)}>Quick Actions</MuiButton>
            <MuiButton onClick={() => setOpen((prev) => !prev)} sx={{ px: 0.5, minWidth: "unset" }}>
              <ArrowDropDownIcon fontSize="small" />
            </MuiButton>
          </ButtonGroup>

          <Popper open={open} anchorEl={anchorRef.current} transition placement="bottom-end" sx={{ zIndex: 1 }}>
            {({ TransitionProps }) => (
              <Grow {...TransitionProps}>
                <Paper elevation={4} sx={{ mt: 0.5 }}>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList dense>
                      {uploadActions.map((action) => (
                        <MenuItem key={action.label} onClick={action.onClick}>
                          <ListItemIcon>{action.icon}</ListItemIcon>
                          <ListItemText>{action.label}</ListItemText>
                        </MenuItem>
                      ))}
                      {uploadActions.length > 0 && createActions.length > 0 && <Divider />}
                      {createActions.map((action) => (
                        <MenuItem key={action.label} onClick={action.onClick}>
                          <ListItemIcon>{action.icon}</ListItemIcon>
                          <ListItemText>{action.label}</ListItemText>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      )}
    </Box>
  );
};
